import React, { Suspense, lazy, useEffect, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';

const LandingPage = lazy(() => import('./pages/LandingPage'));
const Login = lazy(() => import('./pages/auth/Login'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const UserManagement = lazy(() => import('./pages/admin/UserManagement'));
const DeviceDetail = lazy(() => import('./pages/home/devices/DeviceDetail'));
const MainLayout = lazy(() => import('./components/MainLayout'));
const DevicesPage = lazy(() => import('./pages/home/devices/DevicesPage'));
const FirmwaresPage = lazy(() => import('./pages/home/firmwares/FirmwaresPage'));
const KeysPage = lazy(() => import('./pages/home/keys/KeysPage'));
const RotationsPage = lazy(() => import('./pages/home/rotations/RotationsPage'));
const DeviceNetworkPage = lazy(() => import('./pages/DeviceNetworkPage'));
const AIChatbot = lazy(() => import('./components/AI/AIChatbot'));
const ChatHistory = lazy(() => import('./components/AI/ChatHistory'));
const ReportsPage = lazy(() => import('./pages/home/reports/ReportsPage'));
const DatakorumDemoPage = lazy(() => import('./pages/home/datakorum/DatakorumDemoPage'));
const DefaultOtaBatchPage = lazy(() => import('./pages/home/ota/DefaultOtaBatchPage'));

const RouteFallback = () => (
	<div className="min-h-screen bg-black flex items-center justify-center">
		<div className="h-10 w-10 rounded-full border-2 border-emerald-300/20 border-t-emerald-300 animate-spin" />
	</div>
);

const RedirectDevice = () => {
	const { deviceId } = useParams();
	return <Navigate to={`/home/devices/${deviceId}`} replace />;
};

function App() {
	useEffect(() => {
		document.title = 'Q-SAFE';
	}, []);

	return (
		<AuthProvider>
			<Router>
				<div className="App">
					{/* Floating chatbot: hidden on chat-history pages */}
					{
						// local component to decide visibility based on current route
					}
					<HideableChatbot />
					<MobileRouteScrollReset />
					<MobileFocusedControlGuard />
					
					<Suspense fallback={<RouteFallback />}>
						<Routes>
							<Route path="/" element={<LandingPage />} />
							<Route path="/login" element={<Login />} />
							<Route path="/forgot-password" element={<ForgotPassword />} />
							<Route path="/reset-password" element={<ResetPassword />} />
							<Route
								path="/home"
								element={
									<ProtectedRoute requiredAnyPermissions={['device.read', 'device.write']}>
										<MainLayout />
									</ProtectedRoute>
								}
							>
								<Route index element={<DevicesPage />} />
								<Route path="devices" element={<DevicesPage />} />
								<Route path="devices/:deviceId" element={<DeviceDetail />} />
							</Route>

							{/* Independent pages that still use the MainLayout (sidebar) */}
							<Route
								path="/firmwares"
								element={
									<ProtectedRoute requiredAnyPermissions={['firmware.read', 'firmware.write', 'firmware.sign']}>
										<MainLayout>
											<FirmwaresPage />
										</MainLayout>
									</ProtectedRoute>
								}
							/>
							<Route
								path="/keys"
								element={
									<ProtectedRoute requiredAnyPermissions={['key.read', 'key.write', 'key.assign', 'key.rotate']}>
										<MainLayout>
											<KeysPage />
										</MainLayout>
									</ProtectedRoute>
								}
							/>
							<Route
								path="/rotations"
								element={
									<ProtectedRoute requiredAnyPermissions={['key.read', 'key.write', 'key.rotate']}>
										<MainLayout>
											<RotationsPage />
										</MainLayout>
									</ProtectedRoute>
								}
							/>
							<Route
								path="/network"
								element={
									<ProtectedRoute requiredAnyPermissions={['device.read', 'device.write', 'repo.read', 'repo.write']}>
										<MainLayout>
											<DeviceNetworkPage />
										</MainLayout>
									</ProtectedRoute>
								}
							/>
							<Route
								path="/reports"
								element={
									<ProtectedRoute requiredAnyPermissions={['report.read']}>
										<MainLayout>
											<ReportsPage />
										</MainLayout>
									</ProtectedRoute>
								}
							/>
							<Route
								path="/datakorum-demo"
								element={
									<ProtectedRoute>
										<MainLayout>
											<DatakorumDemoPage />
										</MainLayout>
									</ProtectedRoute>
								}
							/>
							<Route
								path="/ota-batches"
								element={
									<ProtectedRoute requiredAnyPermissions={['device.read', 'device.write', 'firmware.write', 'firmware.sign', 'report.read']}>
										<MainLayout>
											<DefaultOtaBatchPage />
										</MainLayout>
									</ProtectedRoute>
								}
							/>
							<Route
								path="/chat-history"
								element={
									<ProtectedRoute>
										<MainLayout>
											<ChatHistory />
										</MainLayout>
									</ProtectedRoute>
								}
							/>
							<Route
								path="/chat-history/:chatId"
								element={
									<ProtectedRoute>
										<MainLayout>
											<ChatHistory />
										</MainLayout>
									</ProtectedRoute>
								}
							/>
							<Route 
								path="/devices/:deviceId"
								element={
									<ProtectedRoute requiredAnyPermissions={['device.read', 'device.write']}>
										{/** redirect old /devices/:id to /home/devices/:id so external links keep working */}
										<RedirectDevice />
									</ProtectedRoute>
								}
							/>

							<Route 
								path="/admin/users" 
								element={
									<ProtectedRoute requiredAnyPermissions={['membership.read', 'membership.write', 'role.read', 'role.write']}>
										<MainLayout>
											<UserManagement />
										</MainLayout>
									</ProtectedRoute>
								} 
							/>
							<Route path="*" element={<Navigate to="/" replace />} />
						</Routes>
					</Suspense>
				</div>
			</Router>
		</AuthProvider>
	);
}

// Component rendered inside Router so we can use useLocation()
const HideableChatbot = () => {
	const location = useLocation();

	// Hide the floating chatbot when the user is on chat-history pages (list or single chat)
	if (location && location.pathname && location.pathname.startsWith('/chat-history')) {
		return null;
	}

	return (
		<Suspense fallback={null}>
			<AIChatbot />
		</Suspense>
	);
};

const MobileRouteScrollReset = () => {
	const { pathname } = useLocation();

	useLayoutEffect(() => {
		if (typeof window === 'undefined' || !window.matchMedia('(max-width: 767px)').matches) {
			return;
		}

		window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
		document.documentElement.scrollTop = 0;
		document.body.scrollTop = 0;
	}, [pathname]);

	return null;
};

const MobileFocusedControlGuard = () => {
	useEffect(() => {
		if (typeof window === 'undefined' || !window.matchMedia) {
			return undefined;
		}

		const mediaQuery = window.matchMedia('(max-width: 767px)');
		const focusableSelector = 'input, textarea, select, [contenteditable="true"]';
		let timeoutId = null;

		const keepFocusedControlVisible = () => {
			if (!mediaQuery.matches) {
				return;
			}

			const activeElement = document.activeElement;
			if (!activeElement || !activeElement.matches?.(focusableSelector)) {
				return;
			}

			activeElement.scrollIntoView({
				block: 'nearest',
				inline: 'nearest',
				behavior: 'auto',
			});
		};

		const scheduleVisibilityCheck = () => {
			window.clearTimeout(timeoutId);
			timeoutId = window.setTimeout(keepFocusedControlVisible, 90);
		};

		window.addEventListener('focusin', scheduleVisibilityCheck);
		window.addEventListener('resize', scheduleVisibilityCheck);
		window.visualViewport?.addEventListener('resize', scheduleVisibilityCheck);

		return () => {
			window.clearTimeout(timeoutId);
			window.removeEventListener('focusin', scheduleVisibilityCheck);
			window.removeEventListener('resize', scheduleVisibilityCheck);
			window.visualViewport?.removeEventListener('resize', scheduleVisibilityCheck);
		};
	}, []);

	return null;
};

export default App;
